import { type MouseEvent } from 'react';

import { useTooltip } from '../components/overlay-tooltip/contexts/TooltipContext.js';
import type { Data, Overlay } from '../components/overlay-tooltip/types.js';
import { useTooltipReducer } from '../components/overlay-tooltip/useTooltipReducer.js';
import { emptyData } from '../components/overlay-tooltip/utils.js';

type Options = {
  onToolbarVisibilityChange: (visible: boolean) => void;
  getTooltipData: (event: MouseEvent<HTMLDivElement>) => Data | undefined;
};

export const useOverlayEventHandlers = (overlay: Overlay, options: Options) => {
  const overlayRef = overlay.overlayRef;
  const tooltipStore = useTooltip();
  const dispatch = useTooltipReducer();
  const { onToolbarVisibilityChange, getTooltipData } = options;

  const handlePointerEnter = () => {
    onToolbarVisibilityChange(true);
  };

  const handlePointerMove = (event: MouseEvent<HTMLDivElement>) => {
    if (
      !overlayRef.current ||
      tooltipStore.getState().pinned ||
      event.target !== overlayRef.current
    ) {
      return;
    }

    const tooltipData = getTooltipData(event);
    if (tooltipData) {
      dispatch({ type: 'SET_POSITION', payload: tooltipData })(
        event.nativeEvent,
      );
    } else {
      dispatch({ type: 'SET_OUT_BOUNDS' })();
    }
  };

  const handlePointerLeave = () => {
    onToolbarVisibilityChange(false);
    dispatch({ type: 'SET_OUT_BOUNDS' })();
  };

  const handleMouseClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if (!overlayRef.current || event.target !== overlayRef.current) {
      return;
    }

    const inBounds = tooltipStore.getState().inBounds;
    const tooltipData = getTooltipData(event);
    const isPinned = tooltipStore.getState().pinned;

    if (inBounds || isPinned) {
      if (isPinned) {
        dispatch({
          type: 'SET_PINNED',
          payload: { pinned: false, data: emptyData() },
        })();

        if (tooltipData) {
          dispatch({ type: 'SET_POSITION', payload: tooltipData })(
            event.nativeEvent,
          );
        }
      } else if (tooltipData) {
        const { offsetX, offsetY } = event.nativeEvent;
        dispatch({
          type: 'SET_PINNED',
          payload: {
            pinned: true,
            data: tooltipData,
            relativeX: offsetX,
            relativeY: offsetY,
          },
        })();
      }
    }
  };

  return {
    handlePointerEnter,
    handlePointerMove,
    handlePointerLeave,
    handleMouseClick,
  };
};
